import React from "react";

import { useField } from "formik";
import { Input, Switch } from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

import CustomLabel from "../customLabel";
import "./styles.scss";
import useWindowDimensions from "../custom-hooks/useWindowDimensions";
import { SMALL_MOBILE_WIDTH } from "../../constants/constants";

const CustomInputField = (props) => {
  const [field, meta] = useField(props);

  const {
    title,
    maxLength,
    className,
    customClass,
    placeholder,
    name,
    autoComplete,
    type,
    width,
    disabled,
    optionalText,
    style,
    required,
    autoFocus,
    allowedRegex,
  } = props;

  const { width: screenWidth } = useWindowDimensions();
  const elements = (
    <>
      <CustomLabel
        title={title}
        customClass={customClass}
        optionalText={optionalText}
        required={required ? true : false}
      />

      <Input
        {...field}
        style={style}
        className={`${className} ${
          meta.touched && meta.error ? "error-field" : ""
        }`}
        placeholder={placeholder}
        name={name}
        type={type}
        autoComplete={autoComplete}
        width={width}
        disabled={disabled}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onChange={(e) => {
          let { value = "" } = e.target;
          e.preventDefault();
          if (!allowedRegex || !value || allowedRegex.test(value)) {
            field.onChange(e);
            if (props.onChange) {
              props.onChange(e); // Call the parent onChange
            }
          }
        }}
      />

      {meta.touched && meta.error ? (
        <div className="error-label">{meta.error}</div>
      ) : null}
    </>
  );

  return screenWidth > SMALL_MOBILE_WIDTH ? (
    elements
  ) : (
    <div className="mobile-view">{elements}</div>
  );
};

const CustomInputFieldWithAddOn = (props) => {
  const [field, meta] = useField(props);

  const {
    title,
    maxLength,
    className,
    customClass,
    placeholder,
    name,
    autoComplete,
    type,
    width,
    disabled,
    optionalText,
    style,
    required,
    addOnText,
  } = props;

  return (
    <>
      <CustomLabel
        title={title}
        customClass={customClass}
        optionalText={optionalText}
        required={required ? true : false}
      />

      <Input
        {...field}
        style={style}
        className={`${className} ${
          meta.touched && meta.error ? "error-field" : ""
        }`}
        placeholder={placeholder}
        addonAfter={addOnText}
        name={name}
        type={type}
        autoComplete={autoComplete}
        width={width}
        disabled={disabled}
        maxLength={maxLength}
      />

      {meta.touched && meta.error ? (
        <div className="error-label">{meta.error}</div>
      ) : null}
    </>
  );
};

const CustomPassword = (props) => {
  const [field, meta] = useField(props);

  const {
    title,
    className,
    customClass,
    required,
    name,
    maxLength,
    theme = "",
    onChange, // custom onChange from parent
    allowedRegex, // optional: if you want to validate input
  } = props;

  const handleChange = (e) => {
    const { value = "" } = e.target;

    // If you have allowedRegex and want to prevent invalid input
    if (!allowedRegex || !value || allowedRegex.test(value)) {
      field.onChange(e); // Formik's onChange
      if (onChange) {
        onChange(e); // Custom onChange from parent
      }
    }
  };

  return (
    <>
      <CustomLabel
        title={title}
        customClass={customClass}
        required={!!required}
      />

      <Input.Password
        {...field}
        placeholder="Enter Password"
        className={`${className} ${
          meta.touched && meta.error ? "error-field" : ""
        }`}
        type="password"
        name={name}
        iconRender={(visible) =>
          visible ? (
            <EyeTwoTone twoToneColor={theme === "nextgen" ? "#c76e4d" : ""} />
          ) : (
            <EyeInvisibleOutlined />
          )
        }
        autoComplete="new-password"
        maxLength={maxLength}
        onChange={handleChange}
      />

      {meta.touched && meta.error ? (
        <div className="error-label">{meta.error}</div>
      ) : null}
    </>
  );
};

const CustomCheckbox = (props) => {
  const [field, meta] = useField(props);

  const {
    title,
    customClass,
    checked,
    required,
    icon,
    name,
    onChange,
    tooltipText,
  } = props;

  return (
    <>
      <CustomLabel
        title={title}
        customClass={customClass}
        required={required ? true : false}
        icon={icon}
        tooltipText={tooltipText}
      />

      <Checkbox
        {...field}
        type="checkbox"
        title={title}
        name={name}
        className={meta.touched && meta.error ? "error-field" : ""}
        onChange={onChange}
        checked={checked}
      />

      {meta.touched && meta.error ? (
        <div className="error-label">{meta.error}</div>
      ) : null}
    </>
  );
};

const CustomTextarea = (props) => {
  const { TextArea } = Input;

  const [field, meta, form] = useField(props);

  const {
    customClass,
    title,
    className,
    name,
    rows,
    placeholder,
    autoComplete,
    required,
    style,
    disabled,
    inputRef,
    readonly,
    allowClear = false,
    hideError = false,
    onChange = undefined,
    errorStyle = "",
    ...rest
  } = props;

  return (
    <>
      <CustomLabel
        title={title}
        customClass={customClass}
        required={required ? true : false}
      />

      <TextArea
        {...rest}
        style={style}
        {...field}
        className={`${className} ${
          meta.touched && meta.error ? "error-field" : ""
        }`}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
          form.setValue(e.target.value);
        }}
        rows={rows}
        placeholder={placeholder}
        name={name}
        autoComplete={autoComplete}
        disabled={disabled}
        ref={inputRef}
        readOnly={readonly}
        allowClear={allowClear}
      />

      {meta.touched && meta.error && !hideError ? (
        <div className={errorStyle ? "error-label-static" : "error-label"}>
          {meta.error}
        </div>
      ) : null}
    </>
  );
};

const CustomSwitch = (props) => {
  const { pretext, name, posttext, required, onChange, title, active } = props;

  return (
    <>
      {title && (
        <CustomLabel title={title} required={required ? true : false} />
      )}
      <div className="custom-switch-wrapper">
        <span className={`pre-text ${active === "false" ? "active" : ""}`}>
          {pretext}
        </span>

        <Switch
          {...props}
          name={name}
          onChange={onChange}
          checked={active === "false" || active === false ? false : true}
        />
        <span className={`post-text ${active === "true" ? "active" : ""}`}>
          {posttext}
        </span>
      </div>
    </>
  );
};

const CustomOtpField = (props) => {
  return <Input {...props} type="text" autoComplete="off" />;
};

const CustomInput = (props) => {
  const { type } = props;

  switch (type) {
    case "checkbox":
      return <CustomCheckbox {...props} />;
    case "password":
      return <CustomPassword {...props} />;
    case "textarea":
      return <CustomTextarea {...props} />;
    case "switch":
      return <CustomSwitch {...props} />;
    case "otp":
      return <CustomOtpField {...props} />;
    case "addOn":
      return <CustomInputFieldWithAddOn {...props} />;
    default:
      return <CustomInputField {...props} />;
  }
};

export default CustomInput;
